import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { refinePrompt, getProject, createDraft, publishDraft, discardDraft, updateDraft } from '../../services/api';
import ModelSelector from '../PromptWizard/ModelSelector';
import { highlightMarkdown } from '../common/MarkdownHighlighter';
import { useModal } from '../../contexts/ModalContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const PromptDetailsView = ({ project }) => {
  return (
    <div className="pd-input-section">
      <div className="pd-card">
        <h3 className="pd-section-title">Description</h3>
        <div className="pd-card-value">
          {project.user_input?.description || 'No description'}
        </div>
      </div>

      <div className="pd-card">
        <h3 className="pd-section-title">Use Case</h3>
        <div className="pd-card-value">
          {project.user_input?.use_case || 'No use case'}
        </div>
      </div>

      <div className="pd-grid">
        <div className="pd-card">
          <h3 className="pd-section-title">Prompt Type</h3>
          <div className="pd-badge">{project.user_input?.type || 'System User'}</div>
          </div>
        
        <div className="pd-card">
          <h3 className="pd-section-title">Output Format</h3>
          <div className="pd-badge">
            {project.user_input?.output_type === 'Schema' ? 'JSON Schema' : (project.user_input?.output_type || 'Plain Text')}
          </div>
        </div>
        
        <div className="pd-card">
          <h3 className="pd-section-title">Model</h3>
          <div className="pd-badge">{project.model || 'Default'}</div>
        </div>
      </div>

      {project.user_input?.input_variables?.length > 0 && (
        <div className="pd-card">
          <h3 className="pd-section-title">Variables</h3>
          <div className="pd-variables">
            {project.user_input.input_variables.map((v, i) => (
              <div key={i} className="pd-variable-row">
                <span className="pd-var-name">{v.name}</span>
                <span className="pd-var-desc">{v.description}</span>
                {v.type && <span className="pd-var-type">{v.type}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {project.user_input?.additional_input_variables?.length > 0 && (
        <div className="pd-card">
          <h3 className="pd-section-title">Optional Variables</h3>
          <div className="pd-variables">
            {project.user_input.additional_input_variables.map((v, i) => (
              <div key={i} className="pd-variable-row system">
                <span className="pd-var-name">{v.name}</span>
                <span className="pd-var-desc">{v.description}</span>
                <span className="pd-var-type">{v.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {project.user_input?.additional_config && (
        project.user_input.additional_config.output_constraints || 
        project.user_input.additional_config.must_include || 
        project.user_input.additional_config.must_avoid || 
        project.user_input.additional_config.special_instructions
      ) && (
        <div className="pd-card">
          <h3 className="pd-section-title">Additional Configuration</h3>
          <div className="pd-additional-config">
            {project.user_input.additional_config.output_constraints && (
              <div className="pd-config-item">
                <span className="pd-config-label">Output Constraints:</span>
                <span className="pd-config-value">{project.user_input.additional_config.output_constraints}</span>
              </div>
            )}
            {project.user_input.additional_config.must_include && (
              <div className="pd-config-item">
                <span className="pd-config-label">Must Include:</span>
                <span className="pd-config-value">{project.user_input.additional_config.must_include}</span>
              </div>
            )}
            {project.user_input.additional_config.must_avoid && (
              <div className="pd-config-item">
                <span className="pd-config-label">Must Avoid:</span>
                <span className="pd-config-value">{project.user_input.additional_config.must_avoid}</span>
              </div>
            )}
            {project.user_input.additional_config.special_instructions && (
              <div className="pd-config-item">
                <span className="pd-config-label">Special Instructions:</span>
                <span className="pd-config-value">{project.user_input.additional_config.special_instructions}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {project.user_input?.schema && (
        <div className="pd-card">
          <h3 className="pd-section-title">JSON Schema</h3>
          <pre className="pd-schema">
            {(() => {
              try {
                const parsed = typeof project.user_input.schema === 'string' 
                  ? JSON.parse(project.user_input.schema) 
                  : project.user_input.schema;
                return JSON.stringify(parsed, null, 2);
              } catch {
                return project.user_input.schema;
              }
            })()}
          </pre>
        </div>
      )}
    </div>
  );
};

const RefinementPage = ({ project: initialProject, onBack }) => {
  const { confirmDiscard } = useModal();
  const { getAvailableProviders } = useWorkspace();
  const [project, setProject] = useState(initialProject);
  const [activeTab, setActiveTab] = useState('current');
  
  const availableProviders = getAvailableProviders();
  const [promptTab, setPromptTab] = useState('system');
  const [feedback, setFeedback] = useState('');
  const [currentStage, setCurrentStage] = useState('input');
  const [analysisData, setAnalysisData] = useState(null);
  const [refinedPrompts, setRefinedPrompts] = useState({
    system: '',
    user: '',
    examples: ''
  });
  const [streamingContent, setStreamingContent] = useState({
    system: '',
    user: '',
    examples: ''
  });
  const [explanation, setExplanation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [selectedModel, setSelectedModel] = useState(project.model);
  const [selectedLLMConfig, setSelectedLLMConfig] = useState(project.llm_config || {});
  
  // Draft management state
  const [draftCommitId, setDraftCommitId] = useState(null);
  const [isDraft, setIsDraft] = useState(false);
  const [showDraftModal, setShowDraftModal] = useState(false);
  
  // Commit message modal state
  const [showCommitModal, setShowCommitModal] = useState(false);
  const [customCommitMessage, setCustomCommitMessage] = useState('');
  const [isGeneratingCommit, setIsGeneratingCommit] = useState(false);

  // Manual edit state
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editedContent, setEditedContent] = useState({
    system: '',
    user: '',
    examples: ''
  });
  const [isSavingDraft, setIsSavingDraft] = useState(false);

  const panelContentRef = useRef(null);

  const promptType = project.user_input?.type || 'System User';
  
  const promptTabs = [];
  if (promptType !== 'User Only' && project.system_prompt_output) {
    promptTabs.push({ id: 'system', label: 'System Prompt' });
  }
  if (project.user_prompt_output) {
    promptTabs.push({ id: 'user', label: 'User Prompt' });
  }
  if (promptType === 'System  User  Assistant ' && project.few_shot_examples) {
    promptTabs.push({ id: 'examples', label: 'Few-Shot Examples' });
  }

  const previewTabs = [...promptTabs, { id: 'changes', label: 'Changes Summary' }];

  useEffect(() => {
    if (promptType === 'User Only') {
      setPromptTab('user');
    } else {
      setPromptTab('system');
    }
  }, [promptType]);
  
  // Browser navigation guard for unsaved drafts and manual edits
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if ((isDraft && currentStage === 'preview') || hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDraft, currentStage, hasUnsavedChanges]);

  const quickSuggestions = [
   "Increase specificity",
    "Add step-by-step instructions",
    "Clarify the objective",
    "Define constraints clearly",
    "Improve structure",
    "Highlight key requirements",
    "Add expected output format",
    "Add context for better accuracy",
    "Include sample inputs",
    "Include sample outputs",
    "Make the tone more friendly",
    "Make the tone more strict",
    "Add domain-specific terminology",
    "Remove ambiguity",
    "Ask for multiple variations",
    "Optimize for speed",
    "Optimize for cost",
    "Add reasoning steps",
    "Add citations or sources",
    "Focus on accuracy",
    "Make it more creative",
    "Reduce creativity",
    "Ensure factual correctness",
    "Add validation rules",
    "Expand on assumptions",
    "Define success criteria",
    "Add formatting instructions",
    "Add fallback handling",
    "Include boundary conditions",
    "Add performance constraints",
    "Add user persona details",
    "Add style guidelines",
    "Make instructions more direct",
    "Split into smaller tasks",
    "Combine similar points",
    "Add checklist format",
    "Request step-by-step reasoning",
    "Add quality checks",
    "Include acceptance criteria",
    "Improve coherence",
    "Add technical depth",
    "Reduce technical complexity"
  ];

  const getSuggestion = () => {
    if (!feedback) return null;
    const lastLine = feedback.split('\n').pop();
    if (!lastLine || lastLine.length === 0) return null;
    
    const match = quickSuggestions.find(s => 
      s.toLowerCase().startsWith(lastLine.toLowerCase())
    );
    return match || null;
  };

  const getCompletionText = () => {
    const suggestion = getSuggestion();
    if (!suggestion) return '';
    
    const lastLine = feedback.split('\n').pop();
    if (suggestion.toLowerCase().startsWith(lastLine.toLowerCase()) && lastLine.length > 0) {
      return suggestion.substring(lastLine.length);
    }
    return '';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      const completion = getCompletionText();
      if (completion) {
        e.preventDefault();
        setFeedback(feedback + completion);
      }
    }
  };

  const handleFeedbackChange = (e) => {
    setFeedback(e.target.value);
  };

  useEffect(() => {
    if (isProcessing && panelContentRef.current) {
      panelContentRef.current.scrollTop = panelContentRef.current.scrollHeight;
    }
  }, [streamingContent, isProcessing]);

  const getCurrentPromptContent = () => {
    switch (promptTab) {
      case 'system': return project.system_prompt_output || '';
      case 'user': return project.user_prompt_output || '';
      case 'examples': return project.few_shot_examples || '';
      default: return '';
    }
  };

  const getRefinedPromptContent = () => {
    if (promptTab === 'changes') return null;
    
    if (currentStage === 'preview') {
      switch (promptTab) {
        case 'system': return refinedPrompts.system || project.system_prompt_output;
        case 'user': return refinedPrompts.user || project.user_prompt_output;
        case 'examples': return refinedPrompts.examples || project.few_shot_examples;
        default: return '';
      }
    } else {
      switch (promptTab) {
        case 'system': return streamingContent.system || '';
        case 'user': return streamingContent.user || '';
        case 'examples': return streamingContent.examples || '';
        default: return '';
      }
    }
  };

  const handleGeneratePreview = async () => {
    if (!feedback.trim()) return;

    // Check permission
    if (!hasEditPermission()) {
      toast.error('You need edit permission to refine this project');
      return;
    }

    // If in edit mode, prompt user to save or discard changes
    if (isEditMode && hasUnsavedChanges) {
      const shouldSave = window.confirm('You have unsaved changes. Save before refining?');
      if (shouldSave) {
        await handleSaveManualEdit();
      }
    }
    
    // Exit edit mode before refining
    if (isEditMode) {
      setIsEditMode(false);
      setHasUnsavedChanges(false);
    }

    // Create draft if it doesn't exist or get existing one
    let currentDraftId = draftCommitId;
    if (!currentDraftId) {
      const result = await createDraft(project.id, project.current_commit_id);
      if (result.success) {
        currentDraftId = result.data.draft_commit_id;
        setDraftCommitId(currentDraftId);
        setIsDraft(true);
      } else {
        // Check if it's a conflict error
        if (result.isConflict) {
          toast.error('⚠️ Someone else modified this project. Please refresh to see the latest version.', { autoClose: 6000 });
        } else {
          toast.error(result.error || 'Failed to create draft');
        }
        return;
      }
    }

    setIsProcessing(true);
    setCurrentStage('analyzing');
    setActiveTab('preview');
    setStreamingContent({ system: '', user: '', examples: '' });
    setRefinedPrompts({ system: '', user: '', examples: '' });
    setExplanation(null);

    refinePrompt(
      project.id,
      feedback,
      selectedModel,
      selectedLLMConfig,
      currentDraftId,
      (stage, data) => {
        setCurrentStage(stage);
        if (stage === 'analysis_complete') {
          setAnalysisData(data.refinement_analysis);
        }
        if (stage === 'refining_system' && promptTabs.find(t => t.id === 'system')) {
          setPromptTab('system');
        } else if (stage === 'refining_user' && promptTabs.find(t => t.id === 'user')) {
          setPromptTab('user');
        } else if (stage === 'refining_examples' && promptTabs.find(t => t.id === 'examples')) {
          setPromptTab('examples');
        }
      },
      (content, promptType) => {
        setStreamingContent(prev => ({
          ...prev,
          [promptType]: prev[promptType] + content
        }));
      },
      null, // onAnalysisChunk - no longer used
      (previewData) => {
        // Map the database field names to frontend field names
        setRefinedPrompts({
          system: previewData.refined_prompts?.system_prompt_output || '',
          user: previewData.refined_prompts?.user_prompt_output || '',
          examples: previewData.refined_prompts?.few_shot_examples || ''
        });
        setExplanation(previewData.change_summary);
        setAnalysisData(previewData.refinement_analysis);
        setCurrentStage('preview');
        setPromptTab('changes'); // Automatically show the changes summary
        setIsProcessing(false);
      },
      (error) => {
        toast.error(`Error: ${error}`);
        setCurrentStage('input');
        setActiveTab('current');
        setIsProcessing(false);
      }
    );
  };

  const handleSave = () => {
    // Check permission
    if (!hasEditPermission()) {
      toast.error('You need edit permission to save changes');
      return;
    }

    // Show commit message modal
    setCustomCommitMessage('');
    setShowCommitModal(true);
  };

  const handleGenerateAICommit = async () => {
    setIsGeneratingCommit(true);
    const result = await publishDraft(project.id, draftCommitId, null, true); // true = generate AI message
    
    if (result.success && result.data.commit_message) {
      setCustomCommitMessage(result.data.commit_message);
      toast.success('AI commit message generated!');
    } else {
      if (result.isConflict) {
        toast.error('⚠️ Conflict: Another user modified this project. Please refresh to see the latest version.', { autoClose: 6000 });
      } else {
        toast.error(result.error || 'Failed to generate commit message');
      }
    }
    setIsGeneratingCommit(false);
  };

  const handleConfirmSave = async () => {
    if (!customCommitMessage.trim()) {
      toast.error('Please provide a commit message or generate one with AI');
      return;
    }

    setIsSaving(true);
    
    // Publish with the custom commit message
    const result = await publishDraft(project.id, draftCommitId, customCommitMessage);

    if (result.success) {
      toast.success(`Version saved: ${customCommitMessage}`, { autoClose: 4000 });
      
      if (result.data.commit_id) {
        toast.info(`Commit ID: ${result.data.commit_id.substring(0, 7)}`, { autoClose: 3000 });
      }

      const updatedProject = await getProject(project.id);
      if (updatedProject.success) {
        setProject(updatedProject.data);
      }

      // Clear all state
      setShowCommitModal(false);
      setCustomCommitMessage('');
      setDraftCommitId(null);
      setIsDraft(false);
      setCurrentStage('input');
      setActiveTab('current');
      setFeedback('');
      setRefinedPrompts({ system: '', user: '', examples: '' });
      setExplanation(null);
      setStreamingContent({ system: '', user: '', examples: '' });
    } else {
      // Check if it's a conflict error
      if (result.isConflict) {
        setShowCommitModal(false);
        toast.error('⚠️ Edit Conflict: Another user published changes while you were editing. Please refresh to see the latest version and merge your changes manually.', { autoClose: 8000 });
      } else {
        toast.error(result.error || 'Failed to save refinement');
      }
    }
    
    setIsSaving(false);
  };

  const handleDiscardDraftChanges = async () => {
    // Check permission
    if (!hasEditPermission()) {
      toast.error('You need edit permission to discard drafts');
      return;
    }

    const confirmed = await confirmDiscard('Discard current refinement? This will delete the draft and cannot be undone.');
    if (!confirmed) {
      return;
    }
    
      // Delete the draft from database if it exists
      if (draftCommitId) {
        const result = await discardDraft(project.id, draftCommitId);
        if (result.success) {
          toast.success('Draft discarded successfully');
          setDraftCommitId(null);
          setIsDraft(false);
        } else {
          toast.error(result.error || 'Failed to discard draft');
          return;
        }
      }
      
      // Reset UI state
      setCurrentStage('input');
      setActiveTab('current');
      setFeedback('');
      setStreamingContent({ system: '', user: '', examples: '' });
      setRefinedPrompts({ system: '', user: '', examples: '' });
      setExplanation(null);
      setAnalysisData(null);
  };
  
  const handleBack = () => {
    // If there's an active draft and we're in preview, show modal
    if (isDraft && currentStage === 'preview') {
      setShowDraftModal(true);
    } else {
      // No draft or not in preview, just go back
      onBack();
    }
  };

  const handleSaveDraft = async () => {
    setShowDraftModal(false);
    await handleSave();
  };

  const handleDiscardDraft = async () => {
    setShowDraftModal(false);
    const result = await discardDraft(project.id, draftCommitId);
    if (result.success) {
      toast.info('Draft discarded');
      onBack();
    } else {
      toast.error(result.error || 'Failed to discard draft');
    }
  };

  const handleCancelModal = () => {
    setShowDraftModal(false);
  };

  // Check if user has edit permission
  const hasEditPermission = () => {
    const permission = project.permission || 'owner';
    return permission === 'owner' || permission === 'edit';
  };

  // Manual edit handlers
  const handleEnableEdit = async () => {
    // Check permission first
    if (!hasEditPermission()) {
      toast.error('You need edit permission to modify this project');
      return;
    }

    // If no draft exists, create one first
    if (!draftCommitId) {
      const result = await createDraft(project.id, project.current_commit_id);
      if (result.success) {
        setDraftCommitId(result.data.draft_commit_id);
        setIsDraft(true);
      } else {
        if (result.isConflict) {
          toast.error('⚠️ Someone else modified this project. Please refresh to see the latest version.', { autoClose: 6000 });
        } else {
          toast.error(result.error || 'Failed to create draft');
        }
        return;
      }
    }
    
    // Initialize edited content with current refined prompts or project prompts
    setEditedContent({
      system: refinedPrompts.system || project.system_prompt_output || '',
      user: refinedPrompts.user || project.user_prompt_output || '',
      examples: refinedPrompts.examples || project.few_shot_examples || ''
    });
    
    // Also set refined prompts so Preview tab has content to show
    if (!refinedPrompts.system && !refinedPrompts.user && !refinedPrompts.examples) {
      setRefinedPrompts({
        system: project.system_prompt_output || '',
        user: project.user_prompt_output || '',
        examples: project.few_shot_examples || ''
      });
    }
    
    // Switch to Preview tab and set stage to preview
    setActiveTab('preview');
    setCurrentStage('preview');
    
    // Set to the first available prompt tab (not 'changes')
    if (promptType !== 'User Only' && project.system_prompt_output) {
      setPromptTab('system');
    } else {
      setPromptTab('user');
    }
    
    setIsEditMode(true);
  };

  const handleEditChange = useCallback((field, value) => {
    setEditedContent(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  }, []);

  const handleSaveManualEdit = async () => {
    if (!hasUnsavedChanges || !draftCommitId) return;
    
    setIsSavingDraft(true);
    
    const result = await updateDraft(
      project.id,
      draftCommitId,
      editedContent.system,
      editedContent.user,
      editedContent.examples
    );
    
    if (result.success) {
      // Update refined prompts with edited content
      setRefinedPrompts({
        system: editedContent.system,
        user: editedContent.user,
        examples: editedContent.examples
      });
      
      // Set stage to preview so Preview tab shows the edited content
      if (currentStage === 'input') {
        setCurrentStage('preview');
      }
      
      setHasUnsavedChanges(false);
      setIsEditMode(false); // Exit edit mode after save
      toast.success('Draft saved');
    } else {
      toast.error(result.error || 'Failed to save draft');
    }
    
    setIsSavingDraft(false);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      const confirmed = window.confirm('You have unsaved changes. Discard them?');
      if (!confirmed) return;
    }
    
    setIsEditMode(false);
    setHasUnsavedChanges(false);
    setEditedContent({
      system: refinedPrompts.system || '',
      user: refinedPrompts.user || '',
      examples: refinedPrompts.examples || ''
    });
  };

  // Auto-save disabled - users must manually click Save
  // useEffect(() => {
  //   // Clear existing timer
  //   if (autoSaveTimerRef.current) {
  //     clearTimeout(autoSaveTimerRef.current);
  //   }
  //   
  //   // Only auto-save if in edit mode and have unsaved changes
  //   if (isEditMode && hasUnsavedChanges && !isSavingDraft) {
  //     autoSaveTimerRef.current = setTimeout(() => {
  //       handleSaveManualEdit();
  //     }, 3000); // Auto-save after 3 seconds of inactivity
  //   }
  //   
  //   return () => {
  //     if (autoSaveTimerRef.current) {
  //       clearTimeout(autoSaveTimerRef.current);
  //     }
  //   };
  // }, [editedContent, hasUnsavedChanges, isEditMode, isSavingDraft]);

  const getStageLabel = () => {
    switch (currentStage) {
      case 'analyzing': return 'Analyzing feedback...';
      case 'analysis_complete': return 'Analysis complete';
      case 'refining_system': return 'Refining system prompt...';
      case 'refining_user': return 'Refining user prompt...';
      case 'refining_examples': return 'Refining examples...';
      case 'preview': return 'Preview ready';
      default: return '';
    }
  };

  return (
    <div className="h-full flex flex-col bg-bg-primary overflow-hidden">
      {/* Header with back button and project info */}
      <div className="pd-header">
        <button onClick={handleBack} className="btn-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Refine
        </button>
        
        <div className="pd-header-content">
          <div className="pd-title-row">
            <h2 className="pd-title">{project.project_name}</h2>
            {isProcessing && <span className="streaming-badge">Processing</span>}
          </div>
          <div className="pd-metadata">
            {project.current_commit_id && (
              <span className="pd-meta-item">
                <span className="pd-meta-label">Version:</span>
                <code className="pd-commit-id">{project.current_commit_id}</code>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Main tabs */}
      <div className="pd-tabs">
        <button
          className={`pd-tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          Input Configuration
        </button>
        <button
          className={`pd-tab ${activeTab === 'current' ? 'active' : ''}`}
          onClick={() => setActiveTab('current')}
        >
          Current Prompts
        </button>
        <button
          className={`pd-tab ${activeTab === 'preview' ? 'active' : ''}`}
          onClick={() => !isProcessing && setActiveTab('preview')}
          disabled={isProcessing && activeTab !== 'preview'}
        >
          {currentStage === 'preview' ? 'Refined Prompts' : 'Preview'}
        </button>
      </div>

      <div className="refinement-layout">
        <div className="refinement-main">
          {activeTab === 'details' && (
            <PromptDetailsView project={project} />
          )}

          {activeTab === 'current' && (
            <div className="prompts-view">
              <div className="output-tabs-container">
                <div className="output-tabs">
                  {promptTabs.map((tab) => (
                    <button
                      key={tab.id}
                      className={`output-tab ${promptTab === tab.id ? 'active' : ''}`}
                      onClick={() => setPromptTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                  {hasEditPermission() && (
                  <button onClick={handleEnableEdit} className="btn-icon-text">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                          <path d="M12 20h9"></path>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                        </svg>
                    <span>Edit</span>
                      </button>
                  )}
                </div>

              <div className="output-panel">
                <pre className="panel-content md-highlighted">
                  {highlightMarkdown(getCurrentPromptContent())}
                </pre>
              </div>
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="prompts-view">
              <div className="output-tabs-container">
              <div className="output-tabs">
                {previewTabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`output-tab ${promptTab === tab.id ? 'active' : ''}`}
                    onClick={() => setPromptTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
                </div>
                {currentStage === 'preview' && !isProcessing && hasEditPermission() && promptTab !== 'changes' && (
                  <div className="flex items-center gap-2">
                    {isEditMode ? (
                      <>
                        {hasUnsavedChanges && (
                          <span className="text-xs text-yellow-400">● Unsaved</span>
                        )}
                        <button onClick={handleSaveManualEdit} className="btn-icon-text" disabled={!hasUnsavedChanges || isSavingDraft}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                            <polyline points="17 21 17 13 7 13 7 21"></polyline>
                            <polyline points="7 3 7 8 15 8"></polyline>
                          </svg>
                          <span>{isSavingDraft ? 'Saving...' : 'Save'}</span>
                        </button>
                        <button onClick={handleCancelEdit} className="btn-icon-text-secondary">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                          <span>Cancel</span>
                        </button>
                      </>
                    ) : (
                      <button onClick={handleEnableEdit} className="btn-icon-text">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5">
                          <path d="M12 20h9"></path>
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                        </svg>
                        <span>Edit</span>
                      </button>
                    )}
                  </div>
                )}
              </div>

              {promptTab === 'changes' ? (
                <div className="output-panel">
                  <div className="panel-content changes-summary-content">
                    {isProcessing && currentStage !== 'preview' ? (
                      <div className="processing-message">
                        <div className="status-indicator"></div>
                        <span>{getStageLabel()}</span>
                      </div>
                    ) : explanation ? (
                      <div className="explanation-content">
                        {explanation.summary && (
                          <div className="summary-section">
                            <h4 className="section-heading">Reasoning</h4>
                            <p className="summary-text">{explanation.summary}</p>
                          </div>
                        )}
                        
                        {analysisData && (
                          <div className="refinement-analysis-section">
                            <h4 className="section-heading">Detailed Analysis</h4>
                            
                            {analysisData.prompts_to_modify && analysisData.prompts_to_modify.length > 0 && (
                              <div className="analysis-prompts-modified">
                                <strong>Modified Prompts:</strong>
                                <ul className="modified-list">
                                  {analysisData.prompts_to_modify.map((prompt, idx) => (
                                    <li key={idx}>{prompt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {analysisData.system_prompt_refinements && analysisData.system_prompt_refinements.length > 0 && (
                              <div className="analysis-refinements">
                                <strong>System Prompt Refinements:</strong>
                                <ul className="refinements-detail-list">
                                  {analysisData.system_prompt_refinements.map((ref, idx) => (
                                    <li key={idx}>{ref}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {analysisData.user_prompt_refinements && analysisData.user_prompt_refinements.length > 0 && (
                              <div className="analysis-refinements">
                                <strong>User Prompt Refinements:</strong>
                                <ul className="refinements-detail-list">
                                  {analysisData.user_prompt_refinements.map((ref, idx) => (
                                    <li key={idx}>{ref}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {analysisData.few_shot_examples_refinements && analysisData.few_shot_examples_refinements.length > 0 && (
                              <div className="analysis-refinements">
                                <strong>Few-Shot Examples Refinements:</strong>
                                <ul className="refinements-detail-list">
                                  {analysisData.few_shot_examples_refinements.map((ref, idx) => (
                                    <li key={idx}>{ref}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="empty-state">No changes summary available yet.</div>
                    )}
                  </div>
                </div>
               ) : (
                 <div className="output-panel">
                   {isEditMode ? (
                     <textarea
                       className="panel-content-editable"
                       value={editedContent[promptTab] || ''}
                       onChange={(e) => handleEditChange(promptTab, e.target.value)}
                       rows={20}
                       placeholder="Enter your prompt content..."
                       disabled={!hasEditPermission()}
                     />
                   ) : (
                     <pre className="panel-content md-highlighted" ref={panelContentRef}>
                       {highlightMarkdown(getRefinedPromptContent())}
                       {isProcessing && currentStage !== 'preview' && <span className="cursor">▌</span>}
                     </pre>
                   )}
                 </div>
               )}
             </div>
           )}
         </div>

         <div className="refinement-sidebar">
           <div className="sidebar-section">
             <h3 className="sidebar-title">Model Configuration</h3>
             <ModelSelector
               model={selectedModel}
               setModel={setSelectedModel}
               llmConfig={selectedLLMConfig}
               setLLMConfig={setSelectedLLMConfig}
               availableProviders={availableProviders}
             />
           </div>

           <div className="sidebar-section feedback-section">
             <h3 className="sidebar-title">Refinement Request</h3>
             <div className="autocomplete-wrapper">
               <textarea
                 className="feedback-textarea"
                 placeholder="Start typing..."
                 value={feedback}
                 onChange={handleFeedbackChange}
                 onKeyDown={handleKeyDown}
                 rows={4}
                 disabled={isProcessing}
               />
               {getCompletionText() && !isProcessing && (
                 <div className="inline-suggestion">
                   {feedback}<span className="suggestion-text">{getCompletionText()}</span>
                 </div>
               )}
             </div>

              <button
                className="btn-generate"
                onClick={handleGeneratePreview}
                disabled={!feedback.trim() || isProcessing || !hasEditPermission()}
                title={!hasEditPermission() ? 'You need edit permission to refine this project' : ''}
              >
<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-4 h-4">
                   <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                </svg>
                {isProcessing ? 'Processing...' : 'Generate Preview'}
              </button>
           </div>

           {currentStage === 'preview' && hasEditPermission() && (
             <div className="sidebar-section">
                <h3 className="sidebar-title">Save Changes</h3>
                <div className="sidebar-actions">
                  <button 
                    className="btn btn-secondary btn-full" 
                    onClick={handleDiscardDraftChanges}
                    disabled={isSaving}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                    Discard Draft
                  </button>
                  <button 
                    className="btn btn-primary btn-full" 
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    {isSaving ? 'Saving...' : 'Save This Version'}
                  </button>
                </div>
             </div>
           )}
         </div>
       </div>

      {/* Draft Confirmation Modal */}
      {showDraftModal && (
        <div className="modal-overlay" onClick={handleCancelModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Unsaved Draft Changes</h3>
              <button className="modal-close" onClick={handleCancelModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p>You have unsaved draft refinements. What would you like to do?</p>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={handleCancelModal}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleDiscardDraft}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                Discard Draft
              </button>
              <button className="btn-primary" onClick={handleSaveDraft} disabled={isSaving}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                {isSaving ? 'Saving...' : 'Save as New Version'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Commit Message Modal */}
      {showCommitModal && (
        <div className="modal-overlay" onClick={() => setShowCommitModal(false)}>
          <div className="modal-content commit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Commit Message</h3>
              <button className="modal-close" onClick={() => setShowCommitModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Provide a commit message to describe your changes, or let AI generate one for you.
              </p>
              
              <div className="commit-input-section">
                <label htmlFor="commit-message">Commit Message</label>
                <textarea
                  id="commit-message"
                  className="commit-message-input"
                  value={customCommitMessage}
                  onChange={(e) => setCustomCommitMessage(e.target.value)}
                  placeholder="e.g., Improved system prompt clarity and added examples"
                  rows={3}
                  maxLength={100}
                />
                <div className="commit-char-count">
                  {customCommitMessage.length}/100 characters
                </div>
              </div>

              <button 
                className="btn-ai-generate" 
                onClick={handleGenerateAICommit}
                disabled={isGeneratingCommit}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                  <polyline points="2 17 12 22 22 17"></polyline>
                  <polyline points="2 12 12 17 22 12"></polyline>
                </svg>
                {isGeneratingCommit ? 'Generating...' : 'Generate with AI'}
              </button>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCommitModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleConfirmSave}
                disabled={isSaving || !customCommitMessage.trim()}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                {isSaving ? 'Saving...' : 'Save Version'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

 PromptDetailsView.propTypes = {
   project: PropTypes.object.isRequired,
 };

RefinementPage.propTypes = {
  project: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};

export default RefinementPage;
